import { useCallback, useEffect, useState } from "react";
import { neon } from "@/integrations/neon";
import { getErrorMessage } from "@/utils/errors";
import type { AnalysisData, AnalysisQuestion, AnalysisSubmission, AnalysisSubmissionAnswer } from "@/features/scripts/types";

interface UseScriptAnalysisOptions {
  scriptFormId: string | null;
}

interface UseScriptAnalysisResult {
  data: AnalysisData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useScriptAnalysis = ({ scriptFormId }: UseScriptAnalysisOptions): UseScriptAnalysisResult => {
  const [data, setData] = useState<AnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!scriptFormId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const submissionsData = await neon.api.reviews.getAnalysis(scriptFormId);

      const submissions: AnalysisSubmission[] = [];
      const questionsMap = new Map<string, AnalysisQuestion>();

      const submissionRows = (submissionsData ?? []) as Array<{
        id: string;
        reviewer_name: string;
        reviewer_email: string;
        notes: string | null;
        submitted_at: string;
        submission_answers: Array<{
          question_id: string;
          score: number;
          form_questions: AnalysisQuestion | null;
        }> | null;
      }>;

      submissionRows.forEach((submission) => {
        const answers: AnalysisSubmissionAnswer[] = (submission.submission_answers ?? [])
          .filter((answer) => !!answer.form_questions)
          .map((answer) => {
            const question = answer.form_questions!;
            if (!questionsMap.has(question.id)) {
              questionsMap.set(question.id, question);
            }
            return {
              question_id: answer.question_id,
              score: answer.score,
              question,
            };
          });

        submissions.push({
          id: submission.id,
          reviewer_name: submission.reviewer_name,
          reviewer_email: submission.reviewer_email,
          notes: submission.notes,
          submitted_at: submission.submitted_at,
          answers,
        });
      });

      const questions = Array.from(questionsMap.values());
      const averages: Record<string, number> = {};

      questions.forEach((question) => {
        const scores = submissions
          .flatMap((submission) => submission.answers)
          .filter((answer) => answer.question_id === question.id)
          .map((answer) => answer.score);

        if (scores.length > 0) {
          averages[question.id] = scores.reduce((total, score) => total + score, 0) / scores.length;
        }
      });

      let weightedOverallScore = 0;
      let totalWeight = 0;

      questions.forEach((question) => {
        if (averages[question.id] !== undefined && question.weight_percentage > 0) {
          const weight = question.weight_percentage / 100;
          weightedOverallScore += averages[question.id] * weight;
          totalWeight += weight;
        }
      });

      if (totalWeight > 0) {
        weightedOverallScore = weightedOverallScore / totalWeight;
      } else if (questions.length > 0) {
        const values = Object.values(averages);
        if (values.length > 0) {
          weightedOverallScore = values.reduce((total, value) => total + value, 0) / values.length;
        }
      }

      setData({
        questions,
        submissions,
        averages,
        weightedOverallScore,
      });
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Unable to load analysis"));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [scriptFormId]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    isLoading,
    error,
    refresh: load,
  };
};
